import React from "react";
import { Icon } from "@/components/Icon";
import { useTranslation } from "react-i18next";

export type ToastType = "success" | "warning" | "info" | "error";

export interface ToastProps {
    id?: string;
    message: React.ReactNode;
    type?: ToastType;
    duration?: number;
    onClose?: () => void;
    className?: string;
}

interface ToastStyleConfig {
    bg: string;
    icon: React.ReactNode;
    closeBtn: string;
}

export const Toast: React.FC<ToastProps> = ({
    message,
    type = "error",
    duration = 4000,
    onClose,
    className = "",
}) => {
    const { t } = useTranslation("global");
    const [isShowing, setIsShowing] = React.useState(false);
    const exitTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
        null
    );

    const handleClose = React.useCallback(() => {
        setIsShowing(false);
        if (onClose) {
            setTimeout(onClose, 300); // Wait for transition duration
        }
    }, [onClose]);

    const startTimer = React.useCallback(() => {
        if (duration > 0) {
            exitTimerRef.current = setTimeout(() => {
                handleClose();
            }, duration);
        }
    }, [duration, handleClose]);

    const stopTimer = React.useCallback(() => {
        if (exitTimerRef.current) {
            clearTimeout(exitTimerRef.current);
            exitTimerRef.current = null;
        }
    }, []);

    React.useEffect(() => {
        // Trigger enter animation on mount
        const enterTimer = setTimeout(() => setIsShowing(true), 10);
        startTimer();

        return () => {
            clearTimeout(enterTimer);
            stopTimer();
        };
    }, [startTimer, stopTimer]);

    const getStyle = (): ToastStyleConfig => {
        switch (type) {
            case "success":
                return {
                    bg: "bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/80 text-emerald-800 dark:text-emerald-300",
                    icon: (
                        <Icon
                            value="checkCircle"
                            size={18}
                            className="shrink-0 text-emerald-600 dark:text-emerald-400"
                        />
                    ),
                    closeBtn:
                        "hover:opacity-80 text-emerald-700 dark:text-emerald-400",
                };
            case "warning":
                return {
                    bg: "bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/80 text-amber-800 dark:text-amber-300",
                    icon: (
                        <Icon
                            value="alertTriangle"
                            size={18}
                            className="shrink-0 text-amber-600 dark:text-amber-400"
                        />
                    ),
                    closeBtn:
                        "hover:opacity-80 text-amber-700 dark:text-amber-400",
                };
            case "info":
                return {
                    bg: "bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-800/80 text-blue-800 dark:text-blue-300",
                    icon: (
                        <Icon
                            value="info"
                            size={18}
                            className="shrink-0 text-blue-600 dark:text-blue-400"
                        />
                    ),
                    closeBtn:
                        "hover:opacity-80 text-blue-700 dark:text-blue-400",
                };
            case "error":
            default:
                return {
                    bg: "bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/80 text-rose-800 dark:text-rose-300",
                    icon: (
                        <Icon
                            value="alertCircle"
                            size={18}
                            className="shrink-0 text-rose-600 dark:text-rose-400"
                        />
                    ),
                    closeBtn:
                        "hover:opacity-80 text-rose-700 dark:text-rose-400",
                };
        }
    };

    const style = getStyle();

    return (
        <div
            onMouseEnter={stopTimer}
            onMouseLeave={startTimer}
            role={type === "error" ? "alert" : "status"}
            aria-atomic="true"
            className={`relative pointer-events-auto w-full shadow-2xl rounded-xl py-3.5 pl-4 pr-10 flex items-center justify-start transition-all duration-300 ease-in-out transform ${
                isShowing
                    ? "translate-y-0 opacity-100 scale-100"
                    : "-translate-y-full opacity-0 scale-95"
            } ${style.bg} ${className}`}
        >
            <div className="flex items-center justify-start text-left gap-2.5 min-w-0">
                <div className="shrink-0 flex items-center justify-center">
                    {style.icon}
                </div>
                <span className="text-xs font-bold leading-tight break-words text-left">
                    {message}
                </span>
            </div>
            {onClose && (
                <button
                    type="button"
                    onClick={handleClose}
                    aria-label={t("common.close")}
                    className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-1 rounded-lg transition-colors cursor-pointer shrink-0 flex items-center justify-center ${style.closeBtn}`}
                >
                    <Icon value="x" size={20} />
                </button>
            )}
        </div>
    );
};

export default Toast;
